/**
 * The ws->invalidation bridge: the StreamEvent relay (/ws/events)
 * connected once per signed-in session. Events carry POINTERS to what
 * changed (the streams-carry-pointers law in the browser): kinds with
 * durable consequences invalidate the affected queries -- the affected
 * views refetch TRUTH from the api. Ephemeral kinds (parsing progress)
 * are re-broadcast to subscribers and never stored.
 */
import type { User } from 'firebase/auth';
import { WS_URL } from './client';
import { queryClient } from './queryClient';

export interface StreamEvent {
  kind: string;
  payload: Record<string, unknown>;
}

type Listener = (event: StreamEvent) => void;

const listeners = new Set<Listener>();
const connectionListeners = new Set<(connected: boolean) => void>();
let connected = false;

function setConnected(value: boolean) {
  connected = value;
  connectionListeners.forEach((cb) => cb(value));
}

/** Durable-consequence kinds -> the queries they stale. */
function invalidate(event: StreamEvent) {
  const projectId = event.payload.project_id;
  if (event.kind === 'parsed') {
    void queryClient.invalidateQueries({ queryKey: ['elements', projectId] });
  }
  // The redline loop live: every materialized compliance run (full
  // pass, drag verdict, fix-all round) stales findings AND elements --
  // fix-all moves geometry, so halos and pipes update together. The
  // illuminance grids stale too: fix-all moves fixtures, and with the
  // heatmap in the default view a stale cache would be visible.
  if (event.kind === 'findings' || event.kind === 'fix_all') {
    void queryClient.invalidateQueries({ queryKey: ['elements', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['findings', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['illuminance', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['compliance-report', projectId] });
  }
  if (event.kind === 'revision_saved') {
    void queryClient.invalidateQueries({ queryKey: ['revisions', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['findings', projectId] });
  }
}

/**
 * Open the relay for a session; returns the close function. The socket
 * reconnects with backoff -- a dropped ws otherwise kills invalidation
 * silently and every view goes stale until a hard reload (seen live:
 * an api restart mid-ingest left the pane at "0 elements" while the
 * server held 1068). Events missed while down are unknowable, so a
 * REconnect invalidates everything: refetch truth, don't guess.
 */
export function connectStream(user: User): () => void {
  let ws: WebSocket | null = null;
  let closed = false;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const open = () => {
    ws = new WebSocket(WS_URL);

    // The server's StreamAuth handshake: the ID token is the REQUIRED
    // first message (browsers can't set headers on websockets).
    ws.onopen = () => {
      const socket = ws;
      void user.getIdToken().then((token) => {
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(JSON.stringify({ token }));
        setConnected(true);
        if (attempt > 0) void queryClient.invalidateQueries();
        attempt = 0;
      });
    };
    // onerror is always followed by onclose -- retry from onclose only.
    ws.onclose = () => {
      setConnected(false);
      if (closed) return;
      attempt += 1;
      timer = setTimeout(open, Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5)));
    };

    ws.onmessage = (message) => {
      let event: StreamEvent;
      try {
        event = JSON.parse(message.data);
      } catch {
        return;
      }
      if (!event?.kind) return;
      event.payload = event.payload ?? {};
      invalidate(event);
      listeners.forEach((cb) => cb(event));
    };
  };

  open();
  return () => {
    closed = true;
    clearTimeout(timer);
    ws?.close();
  };
}

export function subscribeStream(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function subscribeStreamConnected(listener: (connected: boolean) => void): () => void {
  connectionListeners.add(listener);
  listener(connected);
  return () => connectionListeners.delete(listener);
}
